//Service to manage and array of Datasources.  Typically used at the page level.
//TODO:  Need to CODE errorCount and errorMessage.   Also update dataservice-error component

import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { AppConfigService } from './app-config.service';
import { Datasource, DataSourceStatus } from './datasource';
import { DatasourceService } from './datasource.service';

export interface IDataSourceParams {
    objectType: string;
}

@Injectable()
export class DataService {
    public source: Datasource[] = [];

    public hideErrorControl = false; //hides error alerts (dataservice-error component) for this data service

    constructor(private dsService: DatasourceService, private appConfig: AppConfigService) { }

    addDatasource(datasource: Datasource) {
        datasource.datasourceService = this.dsService; //added 5/2/17
        this.source.push(datasource);
    }

    addNewDatasource(objectType?: string, cacheLifeSpan?: any): Datasource {
        let datasource: Datasource = new Datasource();
        datasource.datasourceService = this.dsService; //added 5/2/17
        if (objectType) {
            datasource.objectType = objectType;
        }
        if (cacheLifeSpan) {
            datasource.cacheLifeSpan = cacheLifeSpan;
        } else {
            datasource.cacheLifeSpan = this.appConfig.defaultCacheLifeSpan;
        }
        this.source.push(datasource);
        return datasource;
    }

    deleteDatasource(datasource: Datasource) {
        this.source.forEach((item, index) => {
            if (item === datasource) {
                this.source.splice(index, 1);
            }
        });
        datasource = null;
    }

    public initialize() {
        this.source.length = 0; // clear all exisiting datasources    this is an efficient way to empty an array!!!
    }

    public clearErrors() {
        for (var i in this.source) {
            if (this.source[i].status == 'Error') {
                this.source[i].setStatus(DataSourceStatus.Initialized);
            }
        }
    }

    public get errorCount(): number {
        let count = 0;
        for (var i in this.source) {
            if (this.source[i].status == 'Error' && this.source[i].showErrors) {
                count = count + 1;
            }
        }
        return count;
    }

    public get loadingCount(): number {
        // 1/28/21 Revised to add delay in loading count going to zero if a PROCESS was completed less than 100MS ago.
        //         This is so loading spinner does not temporarily stop between a process and subsequent get
        let count = 0;
        let latestProcessTimestamp: number = 0;

        for (var i in this.source) {
            if (this.source[i].status == 'Loading') {
                count = count + 1;
            }
            let tranType: string = this.source[i].tranType;
            if (tranType != 'DBQueryProcess' && tranType != 'SpreadsheetRatingProcess' && tranType.endsWith('Process')) {
                if (this.source[i].readyTimeStamp && this.source[i].readyTimeStamp > latestProcessTimestamp) {
                    latestProcessTimestamp = this.source[i].readyTimeStamp;
                }
            }
        }
        if (latestProcessTimestamp > 0) {
            // Delay clearing proceessing after a successful PROCESS by 100ms in case there is a subsequent GET 
            // -- Prevents momentary flicker of User Input Blocking between success of PROCESS and start of GET
            if (moment.now() - latestProcessTimestamp < 100) {
                count = count + 1; //fudge and increase processing count by 1 if last successful PROCESS finished less that 100MS ago
            }
        }
        return count;
    }

    // Returns true if any datasource on the page is loading and there has been at least one Process transaction processed prior on the page
    // This is used by the page component to cover the page to prevent user interaction during a Process transaction.
    // Note: This is so user interactions are blocked during both the PROCESS transaction and the subsequent GET transaction.    If/When we make the
    // change for Process to directly return the GET upon success we could change this to only track current Process Events.
    public get isProcessing(): boolean {
        let loadingCount = 0;
        let processTranExists = false;
        let latestProcessTimestamp: number = 0;
        let processCompleteDelay = false;
        for (var i in this.source) {
            if (this.source[i].status == 'Loading') {
                loadingCount = loadingCount + 1;
            }

            let tranType: string = this.source[i].tranType;
            if (tranType != 'DBQueryProcess' && tranType != 'SpreadsheetRatingProcess' && tranType.endsWith('Process')) {
                processTranExists = true;
                if (this.source[i].readyTimeStamp && this.source[i].readyTimeStamp > latestProcessTimestamp) {
                    latestProcessTimestamp = this.source[i].readyTimeStamp;
                }
            }
        }
        if (latestProcessTimestamp > 0) {
            // Delay clearing proceessing after a successful PROCESS by 100ms in case there is a subsequent GET 
            // -- Prevents momentary flicker of User Input Blocking between success of PROCESS and start of GET
            processCompleteDelay = moment.now() - latestProcessTimestamp < 100;
        }
        return processTranExists && (loadingCount > 0 || processCompleteDelay);
    }

    public get refreshableCount(): number {
        let count = 0;
        for (var i in this.source) {
            if (
                this.source[i]._datasourceType == 'SureMGA' &&
                this.source[i].tranType &&
                !(this.source[i]._refreshTranClass == 'Process') &&
                !(this.source[i]._refreshTranClass == 'Template')
            ) {
                count = count + 1;
            }
        }
        return count;
    }

    //refresh all "refreshable" datasources
    public refreshAll(useCache: boolean = true) {
        for (var i in this.source) {
            if (
                this.source[i]._datasourceType == 'SureMGA' &&
                this.source[i].tranType &&
                !(this.source[i]._refreshTranClass == 'Process') &&
                !(this.source[i]._refreshTranClass == 'Template')
            ) {
                this.dsService.refresh(this.source[i], useCache);
            }
        }
    }

    //stop all datasources that are "polling"
    public stopAllRefreshIntervals() {
        for (var i in this.source) {
            if (this.source[i]._refreshInterval > 0 && this.source[i]._intervalID !== null) {
                this.dsService.startRefreshInterval(this.source[i], 0);
            }
        }
    }

    public pauseAllRefreshIntervals() {
        for (var i in this.source) {
            if (this.source[i]._refreshInterval > 0 && this.source[i]._intervalID !== null) {
                // do not pause the epx_user_stats call which is needed to ensure a request requiring authorization occurs
                // between the jwt token expiration and the cookie expiration (the cookie is the transport mechanism for the token for security)
                if (!this.source[i].lastRequestUrl.includes('epx_user_stats')) {
                    this.source[i]._paused = true;
                    this.dsService.startRefreshInterval(this.source[i], this.source[i]._refreshInterval);
                }
            }
        }
    }

    public resumeAllRefreshIntervals() {
        for (var i in this.source) {
            if (this.source[i]._paused) {
                this.source[i]._paused = false;
                this.dsService.startRefreshInterval(this.source[i], this.source[i]._refreshInterval);
            }
        }
    }
}
